import { 
  InsertDriveFile,
  Delete,
  Error,
  FileUpload,
  CheckCircle
} from '@mui/icons-material';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography, Box,
  IconButton,
  CircularProgress,
  Tooltip 
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { PersonalDocumentEmployeeResponse } from '../../../types/personalDocuments';
import { serviceModulesApi } from '../../../api/serviceModulesApi';
import { fileToBase64 } from '../../../common/logic/fileToBase64';
import { GeneralButton } from '../../../common/ui/GeneralButton';
import { ErrorType } from '../../../api/Requests';
import { useTranslation } from 'react-i18next';
interface Props {
  open: boolean;
  onClose: () => void;
  serviceModuleId: number;
  initialFiles: File[];
  employee?: PersonalDocumentEmployeeResponse | null;
}

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

interface FileWithStatus {
  file: File;
  fileSize: string;
  status: UploadStatus;
  errorMessage?: string;
}

export const PersonalDocumentsDialogUpload: React.FC<Props> = ({ open, onClose, serviceModuleId, initialFiles, employee }) => {
  const { t } = useTranslation();
  const [filesWithStatus, setFilesWithStatus] = useState<FileWithStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (open) {
      setFilesWithStatus(initialFiles.map(file => ({ file, status: 'pending', fileSize: formatFileSize(file.size) })));
    }
  }, [open, initialFiles]);

  const handleRemoveFile = (index: number) => {
    setFilesWithStatus(prev => {
      const updatedFiles = prev.filter((_, i) => i !== index);
      
      if (updatedFiles.length === 0) {
        onClose();
      }
      
      return updatedFiles;
    });
  };
  
  const updateFileStatus = (index: number, status: UploadStatus, errorMessage?: string) => {
    setFilesWithStatus(prev => {
      const next = [...prev];
      next[index] = { ...next[index], status: status, errorMessage: errorMessage };
      return next;
    });
  };
  const processFile = async (index: number, signal?: AbortSignal): Promise<boolean> => {
    const fileItem = filesWithStatus[index];
    if (fileItem.status === 'success') return true;

    updateFileStatus(index, 'uploading');

    try {
      const result = employee
          ? await serviceModulesApi.uploadPersonalDocumentToEmployee(
              serviceModuleId, employee.employeeId, fileItem.file, { signal })
          : await serviceModulesApi.uploadPersonalDocumentByFilename(
              serviceModuleId, fileItem.file, { signal });

      if (result.errorType === ErrorType.CancelError) {
        updateFileStatus(index, 'error', 'Aborted');
        return false;
      }
      else if (result.success()) {
        updateFileStatus(index, 'success');
        return true;
      }
      else {
        updateFileStatus(index, 'error', result.errorMessage || 'Upload failed');
        return true;
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.message === 'Aborted') {
        updateFileStatus(index, 'error', 'Aborted');
        return false;
      }
      updateFileStatus(index, 'error', error.message);
      return true;
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);
    abortControllerRef.current = new AbortController();
    
    for (let i = 0; i < filesWithStatus.length; i++) {
      const shouldContinue = await processFile(i, abortControllerRef.current.signal);
      if (!shouldContinue) {
        break;
      }
    }

    setIsUploading(false);
    abortControllerRef.current = null;
  };

  const handleRetryFile = async (index: number) => {
    setIsUploading(true);
    abortControllerRef.current = new AbortController();
    
    await processFile(index, abortControllerRef.current.signal);

    setIsUploading(false);
    abortControllerRef.current = null;
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
  };

  const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
      case 'uploading': return <CircularProgress size={20} />;
      case 'success': return <CheckCircle color="success" fontSize="small" />;
      case 'error': return <Error color="error" fontSize="small" />;
      default: return <InsertDriveFile color="primary" />;
    }
  };

  return (
    <Dialog open={open} onClose={isUploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {employee 
          ? `${t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogUpload.titleEmployee")} ${employee.firstName} ${employee.lastName}` 
          : t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogUpload.titleGeneral")
        }
      </DialogTitle>
      <DialogContent>
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 1,
            py: 1,
            display: 'flex',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Typography variant="caption">
              {t("serviceModulesPage.personalDocumentsPage.shared.documentsTotalcount")}: {filesWithStatus.length} 
            </Typography>
          </Box>
          <List dense sx={{ minHeight: '70px' }}>
            {filesWithStatus.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem 
                  key={item.file.name}
                  secondaryAction={
                    (item.status === 'pending' || item.status === 'error') ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t("buttons.upload")}>
                          <IconButton edge="end" onClick={() => handleRetryFile(index)} size="small" disabled={isUploading}>
                            <FileUpload fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("buttons.delete")}>
                          <IconButton edge="end" onClick={() => handleRemoveFile(index)} color="error" size="small" disabled={isUploading}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : null
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getStatusIcon(item.status)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.file.name} 
                    secondary={item.errorMessage || item.fileSize} 
                    slotProps={{
                      primary: { variant: 'body2', noWrap: true, sx: { pr: 4 } },
                      secondary: { color: item.status === 'error' ? 'error' : 'textSecondary' },
                    }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
      </DialogContent>
      <DialogActions>
        <GeneralButton
          appVariant="secondary"
          onClick={onClose}
          disabled={isUploading}
        >
          {t("buttons.close")}
        </GeneralButton>

        {isUploading ? (
          <GeneralButton 
            onClick={handleCancel} 
            appVariant="danger"
            sx={{ width: "120px" }}
          >
            {t("buttons.cancel")}
          </GeneralButton>
        ) : (
          <GeneralButton 
            onClick={handleUpload} 
            appVariant="primary"
            disabled={filesWithStatus.length === 0 || filesWithStatus.every(f => f.status === 'success')}
            sx={{ width: "120px" }}
          >
            {t("buttons.upload")}
          </GeneralButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
